import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {RefreshCw, Search, ShoppingCart, X} from "lucide-react";
import {HeaderAction, ListLoader, PageHeader} from "../../stock/StockUI";
import useAuthStore from "../../stores/authStore";
import {
    apiErrorMessage, closeJournal, fetchCatalogue, generateInvoice, newId, openJournal, submitSale, WEB_REGISTER_NAME,
} from "./posApi";
import {computeCart, discountRefusal, revalidateDiscounts, round2} from "./posMath";
import {CategoryBar, ProductGrid} from "./PosProductGrid";
import PosCart from "./PosCart";
import {
    ClientPickerModal, DiscountModal, JournalCloseModal, JournalOpenModal, PaymentModal,
    SaleDoneModal, VariantModal,
} from "./PosModals";

// Caisse web : version allégée de la caisse mobile (pos_clone) — produits,
// panier, client, remises, encaissement multi-paiements, session de caisse.
// Pas de tables, cuisine, menus, fidélité ni impression thermique.

// Préférences par navigateur et par compte (employé choisi, session ouverte).
const storageKey = (accountId, name) => `pos_web_${name}_${accountId}`;
const readJson = (key) => {
    try {
        return JSON.parse(localStorage.getItem(key) || "null");
    } catch {
        return null;
    }
};
const writeJson = (key, value) => {
    try {
        if (value === null || value === undefined) localStorage.removeItem(key);
        else localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // stockage indisponible (navigation privée) : on continue sans
    }
};

const emptySale = () => ({id: newId(), lines: [], client: null, note: ""});

const PosPage = () => {
    const account = useAuthStore((s) => s.selectedAccount);
    const user = useAuthStore((s) => s.user);
    const accountId = account?.id;

    const {data: catalogue, isLoading, isFetching, error, refetch} = useQuery({
        queryKey: ["pos-catalogue", accountId],
        queryFn: fetchCatalogue,
        enabled: !!accountId,
        staleTime: 5 * 60 * 1000,
    });

    const [employeeId, setEmployeeId] = useState(() => readJson(storageKey(accountId, "employee")));
    const [journal, setJournal] = useState(() => readJson(storageKey(accountId, "journal")));
    const [sale, setSale] = useState(emptySale);
    const [categoryId, setCategoryId] = useState("all");
    const [query, setQuery] = useState("");
    const [selectedLineId, setSelectedLineId] = useState(null);
    const [modal, setModal] = useState(null); // {type, ...}
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(null); // {saleId, total, change}
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const searchRef = useRef(null);

    const categoriesById = useMemo(
        () => new Map((catalogue?.categories || []).map((c) => [c.id, c])),
        [catalogue],
    );
    const discountsById = useMemo(
        () => new Map((catalogue?.discounts || []).map((d) => [d.id, d])),
        [catalogue],
    );

    // Employé par défaut : celui dont l'email est celui de l'utilisateur connecté.
    useEffect(() => {
        if (!catalogue) return;
        const valid = catalogue.employees.some((e) => e.id === employeeId);
        if (valid) return;
        const mine = catalogue.employees.find((e) => e.email && e.email === user?.email);
        setEmployeeId(mine?.id || catalogue.employees[0]?.id || null);
    }, [catalogue, employeeId, user?.email]);

    useEffect(() => writeJson(storageKey(accountId, "employee"), employeeId), [accountId, employeeId]);
    useEffect(() => writeJson(storageKey(accountId, "journal"), journal), [accountId, journal]);

    // Session : celle mémorisée dans ce navigateur si le serveur ne l'a pas
    // clôturée entre-temps, sinon un journal « Caisse Web » encore ouvert.
    useEffect(() => {
        if (!catalogue) return;
        const server = catalogue.journals.find((j) => j.id === journal?.id);
        if (journal && server && !server.isOpen) {
            setJournal(null);
            return;
        }
        if (!journal) {
            const open = catalogue.journals.find((j) => j.isOpen && j.registerName === WEB_REGISTER_NAME);
            if (open) setJournal({...open, salesTotal: 0});
        }
    }, [catalogue, journal]);

    const totals = useMemo(() => computeCart(sale.lines), [sale.lines]);

    // ── Panier ──────────────────────────────────────────────────────────────

    const addToCart = useCallback((product, variant = null) => {
        const item = variant || product;
        const ruleProduct = {isStockable: item.isStockable, isVendable: item.isVendable};
        setSale((prev) => {
            const existing = prev.lines.find((l) => l.productId === item.id && !l.note && l.price === item.price);
            if (existing) {
                return {...prev, lines: prev.lines.map((l) => (l === existing ? {...l, quantity: l.quantity + 1} : l))};
            }
            return {
                ...prev,
                lines: [...prev.lines, {
                    id: newId(),
                    productId: item.id,
                    name: variant ? `${product.name} — ${variant.name}` : product.name,
                    price: item.price,
                    quantity: 1,
                    taxRate: item.taxRate,
                    note: "",
                    product: ruleProduct,
                    image: product.image || categoriesById.get(product.categoryId)?.image || null,
                    color: product.color || categoriesById.get(product.categoryId)?.color || null,
                    discounts: [],
                }],
            };
        });
    }, [categoriesById]);

    const pickProduct = (product) => {
        if (product.variants.length > 0) setModal({type: "variant", product});
        else addToCart(product);
    };

    const updateLine = (lineId, patch) =>
        setSale((prev) => ({...prev, lines: prev.lines.map((l) => (l.id === lineId ? {...l, ...patch} : l))}));
    const removeLine = (lineId) => setSale((prev) => ({...prev, lines: prev.lines.filter((l) => l.id !== lineId)}));
    const changeQty = (line, delta) => {
        const q = line.quantity + delta;
        if (q <= 0) removeLine(line.id);
        else updateLine(line.id, {quantity: q});
    };

    // Lecture code-barres : Entrée dans la recherche avec un code exact.
    const onSearchKeyDown = (e) => {
        if (e.key !== "Enter" || !catalogue) return;
        const code = query.trim();
        if (!code) return;
        for (const p of catalogue.products) {
            if (p.barcode && p.barcode === code) {
                addToCart(p);
                setQuery("");
                return;
            }
            const v = p.variants.find((x) => x.barcode && x.barcode === code);
            if (v) {
                addToCart(p, v);
                setQuery("");
                return;
            }
        }
    };

    // ── Client & remises ────────────────────────────────────────────────────

    const setClient = (client) => {
        setSale((prev) => {
            const {lines, removed} = revalidateDiscounts(prev.lines, discountsById, client?.clientTypeId || null);
            if (removed.length) {
                toast(`Remise retirée : ${removed.join(", ")} (non éligible pour ce client)`, {icon: "⚠️"});
            }
            return {...prev, client, lines};
        });
        setModal(null);
    };

    const applyDiscount = (discount, target) => {
        const clientTypeId = sale.client?.clientTypeId || null;
        setSale((prev) => ({
            ...prev,
            lines: prev.lines.map((l) => {
                if (target && l.id !== target.id) return l;
                if (discountRefusal(discount, {clientTypeId, product: l.product})) return l;
                // Une remise par ligne : en appliquer une autre la remplace.
                return {
                    ...l,
                    discounts: [{
                        lineDiscountId: newId(),
                        id: discount.id,
                        name: discount.name,
                        value: Number(discount.value) || 0,
                        type: discount.type,
                    }],
                };
            }),
        }));
        setModal(null);
        if (!target) toast.success(`${discount.name} appliquée aux produits éligibles`);
    };

    const clearDiscounts = (target) => {
        setSale((prev) => ({
            ...prev,
            lines: prev.lines.map((l) => (!target || l.id === target.id ? {...l, discounts: []} : l)),
        }));
        setModal(null);
    };

    // ── Session de caisse ───────────────────────────────────────────────────

    const handleOpenJournal = async (startTotal) => {
        setSaving(true);
        try {
            const j = await openJournal({employeeId, startTotal});
            setJournal({...j, salesTotal: 0});
            setModal(null);
            toast.success("Caisse ouverte");
        } catch (e) {
            toast.error(apiErrorMessage(e, "Impossible d'ouvrir la caisse"));
        } finally {
            setSaving(false);
        }
    };

    const handleCloseJournal = async (endTotal) => {
        setSaving(true);
        try {
            await closeJournal({journal, employeeId, endTotal});
            setJournal(null);
            setSale(emptySale());
            setModal(null);
            toast.success("Caisse clôturée");
            refetch();
        } catch (e) {
            toast.error(apiErrorMessage(e, "Impossible de clôturer la caisse"));
        } finally {
            setSaving(false);
        }
    };

    // ── Encaissement ────────────────────────────────────────────────────────

    const handlePay = async (payments, change) => {
        setSaving(true);
        try {
            await submitSale({
                sale: {
                    id: sale.id,
                    total: round2(totals.total),
                    totalHt: round2(totals.ht),
                    totalRemise: round2(totals.discount),
                    note: sale.note,
                    clientId: sale.client?.id || null,
                    employeeId,
                    journalId: journal.id,
                },
                lines: sale.lines,
                payments,
            });
            const paid = payments.reduce((s, p) => s + p.amount, 0);
            setJournal((j) => (j ? {...j, salesTotal: round2((j.salesTotal || 0) + paid)} : j));
            setDone({saleId: sale.id, total: round2(totals.total), change});
            setModal(null);
        } catch (e) {
            // Les ids restent les mêmes : « Valider » à nouveau ré-upserte la vente sans doublon.
            toast.error(apiErrorMessage(e, "La vente n'a pas pu être enregistrée. Réessayez."));
        } finally {
            setSaving(false);
        }
    };

    const newSale = () => {
        setDone(null);
        setSale(emptySale());
        searchRef.current?.focus();
    };

    const openInvoice = async () => {
        if (!done) return;
        setInvoiceLoading(true);
        try {
            const res = await generateInvoice(done.saleId);
            window.open(res.url, "_blank");
        } catch (e) {
            toast.error(apiErrorMessage(e, "Facture indisponible pour le moment (la vente est peut-être encore en cours de traitement)"));
        } finally {
            setInvoiceLoading(false);
        }
    };

    // ── Produits affichés ───────────────────────────────────────────────────

    const visibleProducts = useMemo(() => {
        if (!catalogue) return [];
        const q = query.trim().toLowerCase();
        return catalogue.products.filter((p) => {
            if (categoryId !== "all" && p.categoryId !== categoryId) return false;
            if (!q) return true;
            return p.name.toLowerCase().includes(q) || p.barcode.includes(q) ||
                p.variants.some((v) => v.name.toLowerCase().includes(q) || v.barcode.includes(q));
        });
    }, [catalogue, categoryId, query]);

    // ── Rendu ───────────────────────────────────────────────────────────────

    const editLineNote = (line) => {
        const note = window.prompt("Note de la ligne", line.note);
        if (note !== null) updateLine(line.id, {note: note.trim()});
    };
    const editSaleNote = () => {
        const note = window.prompt("Note de la vente", sale.note);
        if (note !== null) setSale((s) => ({...s, note: note.trim()}));
    };

    if (isLoading || error || !journal) {
        return (
            <div className="min-h-full bg-[#F2F2F7] dark:bg-gray-900 flex flex-col">
                <PageHeader title="Caisse" actions={!isLoading && (
                    <HeaderAction icon={RefreshCw} label="Actualiser" onClick={() => refetch()} disabled={isFetching}/>
                )}/>
                {isLoading ? <ListLoader label="Chargement du catalogue…"/> : error ? (
                    <div className="p-8 text-center text-red-600">{apiErrorMessage(error, "Impossible de charger le catalogue")}</div>
                ) : (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <div className="w-full max-w-sm text-center bg-white dark:bg-gray-800 rounded-[26px] px-7 py-8 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.06)] ring-1 ring-black/[0.06] dark:ring-white/10">
                            <span className="mx-auto w-14 h-14 rounded-full bg-primary/[0.12] text-primary flex items-center justify-center">
                                <ShoppingCart className="w-7 h-7"/>
                            </span>
                            <h2 className="mt-4 text-[17px] font-bold text-gray-900 dark:text-white">La caisse web est fermée</h2>
                            <p className="mt-2 text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">
                                Ouvrez une session avec votre fond de caisse pour commencer à encaisser. Les ventes seront
                                rattachées au journal « {WEB_REGISTER_NAME} ».
                            </p>
                            <button type="button" onClick={() => setModal({type: "open"})}
                                    className="mt-6 w-full py-3 rounded-xl bg-primary text-white text-sm font-bold shadow-[0_6px_16px_rgba(77,182,172,0.35)] hover:bg-primary-dark">
                                Ouvrir la caisse
                            </button>
                        </div>
                    </div>
                )}
                {modal?.type === "open" && catalogue && (
                    <JournalOpenModal employees={catalogue.employees} employeeId={employeeId} onEmployeeChange={setEmployeeId}
                                      saving={saving} onConfirm={handleOpenJournal} onClose={() => setModal(null)}/>
                )}
            </div>
        );
    }

    return (
        <div className="h-[calc(100dvh-4rem)] md:h-[100dvh] flex flex-col lg:flex-row overflow-hidden bg-[#F2F2F7] dark:bg-gray-900">
            {/* ── Catalogue ── */}
            <section className="flex-1 flex flex-col min-w-0 min-h-0">
                <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 flex-shrink-0">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                        <input ref={searchRef} autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                               onKeyDown={onSearchKeyDown} placeholder="Rechercher un produit ou scanner un code-barres"
                               className="w-full h-10 pl-9 pr-9 rounded-xl bg-gray-100 dark:bg-gray-700 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"/>
                        {query && (
                            <button type="button" onClick={() => setQuery("")} aria-label="Effacer"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600">
                                <X className="w-4 h-4"/>
                            </button>
                        )}
                    </div>
                    <span className="hidden sm:inline text-xs text-gray-400 tabular-nums whitespace-nowrap">
                        {visibleProducts.length} produit{visibleProducts.length > 1 ? "s" : ""}
                    </span>
                </div>
                <CategoryBar categories={catalogue.categories} activeId={categoryId} onSelect={setCategoryId}/>
                <ProductGrid products={visibleProducts} categoriesById={categoriesById} onPick={pickProduct}/>
            </section>

            {/* ── Panier ── */}
            <aside className="h-[50%] lg:h-full lg:w-[380px] lg:max-w-[36vw] lg:min-w-[340px] flex-shrink-0 flex flex-col min-h-0 overflow-hidden border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700">
                <PosCart
                    sale={sale} totals={totals}
                    employees={catalogue.employees} employeeId={employeeId} onEmployee={setEmployeeId}
                    selectedLineId={selectedLineId} onSelectLine={setSelectedLineId}
                    onQty={changeQty} onSetQty={(l, q) => updateLine(l.id, {quantity: q})}
                    onLineDiscount={(l) => setModal({type: "discount", target: l})}
                    onLineNote={editLineNote} onRemoveLine={removeLine}
                    onClient={() => setModal({type: "client"})} onRemoveClient={() => setClient(null)}
                    onTicketDiscount={() => setModal({type: "discount", target: null})}
                    onNote={editSaleNote} onClearNote={() => setSale((s) => ({...s, note: ""}))}
                    onClear={() => window.confirm("Vider le panier ?") && setSale(emptySale())}
                    onPay={() => setModal({type: "pay"})}
                    onRefresh={() => refetch()} refreshing={isFetching}
                    onCloseJournal={() => setModal({type: "close"})}
                />
            </aside>

            {modal?.type === "close" && (
                <JournalCloseModal journal={journal} salesTotal={journal?.salesTotal || 0} saving={saving}
                                   onConfirm={handleCloseJournal} onClose={() => setModal(null)}/>
            )}
            {modal?.type === "variant" && (
                <VariantModal product={modal.product} onClose={() => setModal(null)}
                              onPick={(v) => {
                                  addToCart(modal.product, v);
                                  setModal(null);
                              }}/>
            )}
            {modal?.type === "client" && (
                <ClientPickerModal clients={catalogue.clients} selectedId={sale.client?.id}
                                   onSelect={setClient} onClear={() => setClient(null)} onClose={() => setModal(null)}/>
            )}
            {modal?.type === "discount" && (
                <DiscountModal discounts={catalogue.discounts} lines={sale.lines}
                               target={modal.target ? sale.lines.find((l) => l.id === modal.target.id) : null}
                               clientTypeId={sale.client?.clientTypeId || null}
                               onApply={(d) => applyDiscount(d, modal.target)}
                               onClearAll={() => clearDiscounts(modal.target)}
                               onClose={() => setModal(null)}/>
            )}
            {modal?.type === "pay" && (
                <PaymentModal total={round2(totals.total)} methods={catalogue.payments} saving={saving}
                              onClose={() => setModal(null)}
                              onConfirm={handlePay}/>
            )}
            {done && (
                <SaleDoneModal total={done.total} change={done.change} invoiceLoading={invoiceLoading}
                               onInvoice={openInvoice} onNewSale={newSale}/>
            )}
        </div>
    );
};

export default PosPage;
